import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect } from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate } from 'react-router-dom';
import FluxpromptLogo from '../../assets/icons/fluxprompt-logo.svg';
import { useAuthStore } from '../../store/storeAuth';
import { PeekingView } from './Components/PeekingView';
import { SideBySideView } from './Components/SideBySideView';
import { SwapPageView } from './Components/SwapPageView';
import {
  PublishedFormProvider,
  usePublishedForm,
} from './Context/PublishedFormContext';

function NewPublishedFormContent({ isPreview }: { isPreview: boolean }) {
  const navigate = useNavigate();
  const {
    publishedForm,
    publishedNodes,
    pages,
    errorType,
    isLoadingForm,
    isSubmitting,
  } = usePublishedForm();

  const user = useAuthStore(state => state.user);

  const pageOrientation =
    publishedForm?.formStyle?.pageOrientation || 'SWAP_PAGE';
  const isSwapPage = pageOrientation === 'SWAP_PAGE';
  const isSideBySide = pageOrientation === 'SIDE_BY_SIDE';
  const isPeeking = pageOrientation === 'PEEKING';

  useEffect(() => {
    if (isSubmitting) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSubmitting]);

  if (isLoadingForm || !publishedForm || !publishedNodes) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1001,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorType && !isPreview) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1001,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            gap: '30px',
            p: '20px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            height: '400px',
          }}
        >
          <Box>
            <img
              style={{
                maxWidth: '350px',
                width: '100%',
                height: '100px',
              }}
              src={FluxpromptLogo}
              alt="Fluxprompt Logo"
            />
          </Box>

          {errorType === 'login' && (
            <>
              <Typography mb={'24px'} variant="h1">
                You need to be logged in to access this interface
              </Typography>

              <Button
                onClick={() => {
                  navigate('/login');
                }}
                variant="contained"
                fullWidth
              >
                Login
              </Button>
            </>
          )}

          {(errorType === 'notPublished' || errorType === 'notFound') && (
            <>
              <Typography variant="h1">
                Sorry, this interface is not published or its not available
              </Typography>

              <Button
                onClick={() => {
                  navigate('/login');
                }}
                variant="contained"
                fullWidth
              >
                {user?.id ? 'Go To Dashboard' : 'Login '}
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        overflowY: 'auto',
        overflowX: isSideBySide || isPeeking ? 'auto' : 'hidden',
        backgroundColor: publishedForm.formStyle?.backgroundColor
          ? publishedForm.formStyle.backgroundColor
          : '#F9FAFB',
        backgroundImage: publishedForm.formStyle?.backgroundImage
          ? `url(${publishedForm.formStyle.backgroundImage})`
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isSideBySide
            ? {
                xs: '100%',
                md: 'auto',
              }
            : isPeeking
            ? {
                xs: '100%',
                md: 'auto',
              }
            : '1300px',
          display: 'flex',
          justifyContent: 'center',
          padding: 0,
          margin: 0,
        }}
      >
        {isSideBySide ? (
          <SideBySideView
            publishedNodes={publishedNodes || []}
            pages={pages || []}
          />
        ) : isPeeking ? (
          <PeekingView
            publishedNodes={publishedNodes || []}
            pages={pages || []}
          />
        ) : (
          <SwapPageView
            publishedNodes={publishedNodes || []}
            pages={pages || []}
            isSwapPage={isSwapPage}
          />
        )}

        {isSubmitting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
              backdropFilter: 'blur(2px)',
            }}
            onClick={e => e.preventDefault()}
            onWheel={e => e.preventDefault()}
            onTouchMove={e => e.preventDefault()}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'white',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="h6" sx={{ color: '#1F2937' }}>
                {publishedForm.formSettings?.loadingMessage &&
                publishedForm.formSettings?.loadingMessage !== ''
                  ? publishedForm.formSettings.loadingMessage
                  : 'Processing...'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
}

export default function NewPublishedForm({
  isPreview = false,
}: {
  isPreview?: boolean;
}) {
  return (
    <PublishedFormProvider isPreview={isPreview}>
      <NewPublishedFormContent isPreview={isPreview} />
    </PublishedFormProvider>
  );
}
